import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Bounds, EditorItem } from '../../Reducers/Configurator/types';

export type VisibleRect = Bounds;

export type ViewportSize = {
  width: number;
  height: number;
};

const ITEM_SCREEN_FRACTION = 0.7;
const MAX_ZOOM_IN_FRACTION = 0.05;
const VIEW_TRANSITION_MS = 700;
const OVERFLOW_GUARD_IDLE_MS = 700;

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

function lerpVisibleRect(from: VisibleRect, to: VisibleRect, t: number): VisibleRect {
  return {
    x: from.x + (to.x - from.x) * t,
    y: from.y + (to.y - from.y) * t,
    width: from.width + (to.width - from.width) * t,
    height: from.height + (to.height - from.height) * t,
  };
}

/** Visible world rect that fits the full canvas in the viewport, centered with letterboxing. */
export function rectFitCanvas(canvasW: number, canvasH: number, viewport: ViewportSize): VisibleRect {
  if (!viewport.width || !viewport.height || !canvasW || !canvasH) {
    return { x: 0, y: 0, width: canvasW || 1, height: canvasH || 1 };
  }

  const vpAspect = viewport.height / viewport.width;
  const canvasAspect = canvasH / canvasW;

  let width: number;
  let height: number;

  if (canvasAspect > vpAspect) {
    height = canvasH;
    width = height / vpAspect;
  } else {
    width = canvasW;
    height = width * vpAspect;
  }

  return {
    x: (canvasW - width) / 2,
    y: (canvasH - height) / 2,
    width,
    height,
  };
}

function minVisibleWidth(canvasW: number, canvasH: number): number {
  return Math.max(Math.min(canvasW, canvasH) * MAX_ZOOM_IN_FRACTION, 10);
}

function clampViewRect(
  rect: VisibleRect,
  canvasW: number,
  canvasH: number,
  viewport: ViewportSize
): VisibleRect {
  if (!viewport.width || !viewport.height) {
    return rect;
  }

  const fit = rectFitCanvas(canvasW, canvasH, viewport);
  const vpAspect = viewport.height / viewport.width || 1;
  const minWidth = minVisibleWidth(canvasW, canvasH);
  const maxWidth = fit.width;

  let width = Math.max(minWidth, Math.min(rect.width, maxWidth));
  const height = width * vpAspect;

  if (width >= maxWidth - 1e-9) {
    return { ...fit };
  }

  let { x, y } = rect;
  const minX = Math.min(0, canvasW - width);
  const maxX = Math.max(0, canvasW - width);
  const minY = Math.min(0, canvasH - height);
  const maxY = Math.max(0, canvasH - height);

  x = Math.max(minX, Math.min(x, maxX));
  y = Math.max(minY, Math.min(y, maxY));

  return { x, y, width, height };
}

function itemDisplayHeight(item: EditorItem): number {
  return item.height;
}

const BOUNDS_VISIBLE_EPS = 1e-6;

/** True when bounds lie entirely inside the current visible world rect. */
export function boundsFullyVisible(bounds: Bounds, viewRect: VisibleRect): boolean {
  if (!bounds.width || !bounds.height) {
    return true;
  }

  return (
    bounds.x >= viewRect.x - BOUNDS_VISIBLE_EPS &&
    bounds.y >= viewRect.y - BOUNDS_VISIBLE_EPS &&
    bounds.x + bounds.width <= viewRect.x + viewRect.width + BOUNDS_VISIBLE_EPS &&
    bounds.y + bounds.height <= viewRect.y + viewRect.height + BOUNDS_VISIBLE_EPS
  );
}

/** Fit a world-space bounds rect in the viewport, centered with padding. */
export function rectForBounds(
  bounds: Bounds,
  viewport: ViewportSize,
  canvasW: number,
  canvasH: number,
  screenFraction = ITEM_SCREEN_FRACTION
): VisibleRect {
  if (!bounds.width || !bounds.height) {
    return rectFitCanvas(canvasW, canvasH, viewport);
  }

  const aspect = viewport.height / viewport.width || 1;
  let width = bounds.width / screenFraction;
  let height = width * aspect;

  if (bounds.height / height > screenFraction) {
    height = bounds.height / screenFraction;
    width = height / aspect;
  }

  const minWidth = minVisibleWidth(canvasW, canvasH);
  if (width < minWidth) {
    width = minWidth;
    height = width * aspect;
  }

  const centerX = bounds.x + bounds.width / 2;
  const centerY = bounds.y + bounds.height / 2;

  return clampViewRect(
    {
      x: centerX - width / 2,
      y: centerY - height / 2,
      width,
      height,
    },
    canvasW,
    canvasH,
    viewport
  );
}

export function itemBounds(item: EditorItem): Bounds {
  return {
    x: item.x,
    y: item.y,
    width: item.width,
    height: itemDisplayHeight(item),
  };
}

export function itemBoundsKey(item: EditorItem): string {
  return `${item.x}:${item.y}:${item.width}:${item.height}`;
}

export function rectForItem(
  item: EditorItem,
  viewport: ViewportSize,
  canvasW: number,
  canvasH: number
): VisibleRect {
  return rectForBounds(itemBounds(item), viewport, canvasW, canvasH);
}

/** Initial fit for width-locked text: fit full bounds (width and height) with padding, centered on the item. */
export function rectForWidthLockedTextSelect(
  item: EditorItem,
  viewport: ViewportSize,
  canvasW: number,
  canvasH: number
): VisibleRect {
  return rectForBounds(itemBounds(item), viewport, canvasW, canvasH);
}

function visibleRectsEqual(a: VisibleRect, b: VisibleRect): boolean {
  return (
    Math.abs(a.x - b.x) <= BOUNDS_VISIBLE_EPS &&
    Math.abs(a.y - b.y) <= BOUNDS_VISIBLE_EPS &&
    Math.abs(a.width - b.width) <= BOUNDS_VISIBLE_EPS &&
    Math.abs(a.height - b.height) <= BOUNDS_VISIBLE_EPS
  );
}

function panRectToIncludeBounds(bounds: Bounds, rect: VisibleRect): VisibleRect {
  let { x, y, width, height } = rect;

  if (bounds.x < x) {
    x = bounds.x;
  }
  if (bounds.y < y) {
    y = bounds.y;
  }
  if (bounds.x + bounds.width > x + width) {
    x = bounds.x + bounds.width - width;
  }
  if (bounds.y + bounds.height > y + height) {
    y = bounds.y + bounds.height - height;
  }

  return { x, y, width, height };
}

/** Pan or zoom out when bounds overflow the view; never zoom in or refit-to-center. */
export function rectRevealOverflow(
  bounds: Bounds,
  current: VisibleRect,
  viewport: ViewportSize,
  canvasW: number,
  canvasH: number
): VisibleRect | null {
  if (boundsFullyVisible(bounds, current)) {
    return null;
  }

  const panned = clampViewRect(
    panRectToIncludeBounds(bounds, current),
    canvasW,
    canvasH,
    viewport
  );
  if (boundsFullyVisible(bounds, panned)) {
    return visibleRectsEqual(panned, current) ? null : panned;
  }

  const vpAspect = viewport.height / viewport.width || 1;
  const width = Math.max(
    current.width,
    bounds.width,
    bounds.height / vpAspect,
    bounds.x + bounds.width - current.x,
    (bounds.y + bounds.height - current.y) / vpAspect
  );
  const height = width * vpAspect;

  let revealed = clampViewRect(
    { x: current.x, y: current.y, width, height },
    canvasW,
    canvasH,
    viewport
  );
  revealed = clampViewRect(panRectToIncludeBounds(bounds, revealed), canvasW, canvasH, viewport);

  return visibleRectsEqual(revealed, current) ? null : revealed;
}

function rectForSelectedItemOnSelect(
  item: EditorItem,
  viewport: ViewportSize,
  canvasW: number,
  canvasH: number
): VisibleRect {
  return rectForItem(item, viewport, canvasW, canvasH);
}

export function visibleRectToTransform(rect: VisibleRect, viewport: ViewportSize) {
  if (!viewport.width || !viewport.height) {
    return { pxPerMm: 1, transform: '' };
  }
  const pxPerMm = viewport.width / rect.width;
  const translateX = -rect.x * pxPerMm;
  const translateY = -rect.y * pxPerMm;
  return {
    pxPerMm,
    transform: `translate(${translateX}, ${translateY}) scale(${pxPerMm})`,
  };
}

type ViewportTargetFn = (viewport: ViewportSize) => VisibleRect;

export function useEditorViewport({
  canvasW,
  canvasH,
  objects,
  selectedId,
  designSize,
  viewport,
  backgroundIsRoom,
}: {
  canvasW: number;
  canvasH: number;
  objects: Record<string, EditorItem>;
  selectedId: string | null;
  designSize: Bounds;
  viewport: ViewportSize;
  backgroundIsRoom: boolean;
}) {
  const [viewRect, setViewRect] = useState<VisibleRect>(() => rectFitCanvas(canvasW, canvasH, viewport));
  const [autoAnimating, setAutoAnimating] = useState(false);
  const viewRectRef = useRef(viewRect);
  viewRectRef.current = viewRect;
  const animationFrameRef = useRef<number | null>(null);
  const animationViewportRef = useRef<ViewportSize | null>(null);
  const prevSelectedId = useRef<string | null>(null);
  const prevBoundsKeyRef = useRef<string | null>(null);
  const didInitialViewRef = useRef(false);
  const prevBackgroundIsRoomRef = useRef(backgroundIsRoom);
  const canvasSizeKeyRef = useRef(`${canvasW}:${canvasH}`);
  const viewportReadyRef = useRef(false);
  const prevViewportRef = useRef<ViewportSize>({ width: 0, height: 0 });
  const overflowGuardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const objectsRef = useRef(objects);
  objectsRef.current = objects;
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const cancelOverflowGuardTimer = useCallback(() => {
    if (overflowGuardTimerRef.current !== null) {
      clearTimeout(overflowGuardTimerRef.current);
      overflowGuardTimerRef.current = null;
    }
  }, []);

  const cancelViewAnimation = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    animationViewportRef.current = null;
    setAutoAnimating(false);
  }, []);

  const animateViewRectTo = useCallback(
    (getTarget: ViewportTargetFn) => {
      cancelViewAnimation();

      const frozenViewport = { width: viewport.width, height: viewport.height };
      animationViewportRef.current = frozenViewport;
      setAutoAnimating(true);

      animationFrameRef.current = requestAnimationFrame(() => {
        const from = viewRectRef.current;
        const target = getTarget(frozenViewport);
        const start = performance.now();

        const tick = (now: number) => {
          const t = easeOutCubic(Math.min(1, (now - start) / VIEW_TRANSITION_MS));
          setViewRect(lerpVisibleRect(from, target, t));

          if (t < 1) {
            animationFrameRef.current = requestAnimationFrame(tick);
          } else {
            animationFrameRef.current = null;
            animationViewportRef.current = null;
            setAutoAnimating(false);
            setViewRect((prev) => clampViewRect(target, canvasW, canvasH, viewport));
          }
        };

        tick(start);
      });
    },
    [cancelViewAnimation, canvasW, canvasH, viewport.width, viewport.height]
  );

  useEffect(
    () => () => {
      cancelViewAnimation();
      cancelOverflowGuardTimer();
    },
    [cancelViewAnimation, cancelOverflowGuardTimer]
  );

  const scheduleOverflowGuard = useCallback(() => {
    cancelOverflowGuardTimer();
    overflowGuardTimerRef.current = setTimeout(() => {
      overflowGuardTimerRef.current = null;

      const id = selectedIdRef.current;
      if (!id) return;

      const item = objectsRef.current[id];
      if (!item) return;

      const target = rectRevealOverflow(
        itemBounds(item),
        viewRectRef.current,
        { width: viewport.width, height: viewport.height },
        canvasW,
        canvasH
      );
      if (!target) return;

      animateViewRectTo(() => target);
    }, OVERFLOW_GUARD_IDLE_MS);
  }, [
    cancelOverflowGuardTimer,
    animateViewRectTo,
    viewport.width,
    viewport.height,
    canvasW,
    canvasH,
  ]);

  const selectedItem = selectedId ? objects[selectedId] : null;
  const selectedBoundsKey = selectedItem ? itemBoundsKey(selectedItem) : null;

  const frameSelectedOrDesign = useCallback(
    (vp: ViewportSize) =>
      selectedItem
        ? rectForSelectedItemOnSelect(selectedItem, vp, canvasW, canvasH)
        : rectForBounds(designSize, vp, canvasW, canvasH),
    [selectedItem, designSize, canvasW, canvasH]
  );

  useLayoutEffect(() => {
    if (!viewport.width || !viewport.height) return;

    const backgroundRoomChanged = backgroundIsRoom !== prevBackgroundIsRoomRef.current;
    prevBackgroundIsRoomRef.current = backgroundIsRoom;

    // First framing: a room scene is an environment the customer wants to see whole, so
    // keep the fit-canvas view. A flat wall / no backdrop keeps the classic product view
    // that frames the auto-selected item.
    if (!didInitialViewRef.current) {
      didInitialViewRef.current = true;
      prevSelectedId.current = selectedId;
      prevBoundsKeyRef.current = selectedBoundsKey;
      if (!backgroundIsRoom && selectedId && selectedItem) {
        cancelOverflowGuardTimer();
        animateViewRectTo((vp) => rectForSelectedItemOnSelect(selectedItem, vp, canvasW, canvasH));
      }
      return;
    }

    // Background type switched: a room reveals the whole scene, leaving a room frames the item.
    if (backgroundRoomChanged) {
      cancelOverflowGuardTimer();
      if (backgroundIsRoom) {
        animateViewRectTo((vp) => rectFitCanvas(canvasW, canvasH, vp));
      } else {
        animateViewRectTo(frameSelectedOrDesign);
      }
      prevSelectedId.current = selectedId;
      prevBoundsKeyRef.current = selectedBoundsKey;
      return;
    }

    if (selectedId && selectedItem) {
      if (selectedId !== prevSelectedId.current) {
        cancelOverflowGuardTimer();
        animateViewRectTo((vp) => rectForSelectedItemOnSelect(selectedItem, vp, canvasW, canvasH));
      } else if (prevBoundsKeyRef.current !== null && selectedBoundsKey !== prevBoundsKeyRef.current) {
        scheduleOverflowGuard();
      }
    } else if (prevSelectedId.current && !selectedId) {
      cancelOverflowGuardTimer();
      const from = viewRectRef.current;
      const fit = rectFitCanvas(canvasW, canvasH, viewport);
      if (from.width < fit.width - 1e-9) {
        animateViewRectTo((vp) => rectFitCanvas(canvasW, canvasH, vp));
      }
    }

    prevSelectedId.current = selectedId;
    prevBoundsKeyRef.current = selectedBoundsKey;
  }, [
    selectedId,
    selectedItem,
    selectedBoundsKey,
    backgroundIsRoom,
    designSize.x,
    designSize.y,
    designSize.width,
    designSize.height,
    viewport.width,
    viewport.height,
    canvasW,
    canvasH,
    animateViewRectTo,
    cancelOverflowGuardTimer,
    scheduleOverflowGuard,
    frameSelectedOrDesign,
  ]);

  useEffect(() => {
    if (!viewport.width || !viewport.height) return;
    if (autoAnimating) return;

    const newFit = rectFitCanvas(canvasW, canvasH, viewport);
    const sizeKey = `${canvasW}:${canvasH}`;
    const sizeChanged = canvasSizeKeyRef.current !== sizeKey;
    const firstViewport = !viewportReadyRef.current;
    const viewportChanged =
      prevViewportRef.current.width !== viewport.width || prevViewportRef.current.height !== viewport.height;

    if (firstViewport || sizeChanged) {
      viewportReadyRef.current = true;
      canvasSizeKeyRef.current = sizeKey;
      prevViewportRef.current = { width: viewport.width, height: viewport.height };
      setViewRect(newFit);
      return;
    }

    if (viewportChanged) {
      const oldFit = rectFitCanvas(canvasW, canvasH, prevViewportRef.current);
      prevViewportRef.current = { width: viewport.width, height: viewport.height };

      setViewRect((prev) => {
        if (prev.width >= oldFit.width - 1e-9) {
          return newFit;
        }
        return clampViewRect(prev, canvasW, canvasH, viewport);
      });
    }
  }, [canvasW, canvasH, viewport.width, viewport.height, autoAnimating]);

  const panByPixels = useCallback(
    (dxPx: number, dyPx: number) => {
      cancelOverflowGuardTimer();
      cancelViewAnimation();
      setViewRect((prev) => {
        const fit = rectFitCanvas(canvasW, canvasH, viewport);
        if (prev.width >= fit.width - 1e-9) {
          return fit;
        }

        const pxPerMm = viewport.width / prev.width;
        return clampViewRect(
          {
            ...prev,
            x: prev.x - dxPx / pxPerMm,
            y: prev.y - dyPx / pxPerMm,
          },
          canvasW,
          canvasH,
          viewport
        );
      });
    },
    [viewport.width, viewport.height, canvasW, canvasH, cancelViewAnimation, cancelOverflowGuardTimer]
  );

  const zoomAtPoint = useCallback(
    (clientX: number, clientY: number, svgRect: DOMRect, zoomFactor: number) => {
      cancelOverflowGuardTimer();
      cancelViewAnimation();
      setViewRect((prev) => {
        const fit = rectFitCanvas(canvasW, canvasH, viewport);
        const pxPerMm = viewport.width / prev.width;
        const localX = clientX - svgRect.left;
        const localY = clientY - svgRect.top;
        const worldX = prev.x + localX / pxPerMm;
        const worldY = prev.y + localY / pxPerMm;

        const aspect = viewport.height / viewport.width || 1;
        const minWidth = minVisibleWidth(canvasW, canvasH);
        let newWidth = prev.width / zoomFactor;
        newWidth = Math.max(minWidth, Math.min(newWidth, fit.width));
        const newHeight = newWidth * aspect;
        const newPxPerMm = viewport.width / newWidth;

        return clampViewRect(
          {
            x: worldX - localX / newPxPerMm,
            y: worldY - localY / newPxPerMm,
            width: newWidth,
            height: newHeight,
          },
          canvasW,
          canvasH,
          viewport
        );
      });
    },
    [viewport.width, viewport.height, canvasW, canvasH, cancelViewAnimation, cancelOverflowGuardTimer]
  );

  const transformViewport = animationViewportRef.current ?? viewport;
  const { pxPerMm, transform } = visibleRectToTransform(viewRect, transformViewport);

  return {
    viewRect,
    pxPerMm,
    transform,
    panByPixels,
    zoomAtPoint,
  };
}
